"use client";
import { CheckCircle2 as CheckIcon, ChevronRightIcon, MinusIcon } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import React from "react";
import { getButtonVariantStylesAndSize, ButtonVariantTypes } from "components/ui/button";
import { APP_LANGUAGES } from "utils/language";
import Contact from "../../commonComponents/Contact";
interface Tier {
  name: string;
  id: string;
  iconUrl: string;
  href: string;
  stripeLink?: string;
  featured: boolean;
  description: string;
  price?: { total: string; discounted: string };
  mainFeatures: string[];
}

interface Feature {
  name: string;
  tiers?: {
    [key: string]: {
      content: boolean | string;
      icon?: string; // Optional icon URL
    };
  };
}

interface Section {
  name: string;
  features?: Feature[];
}

interface PackageComparisonProps {
  title: string;
  subtitle: string;
  contactLink?: string;
  tiers?: Tier[];
  sections?: Section[];
  language?: APP_LANGUAGES;
}

function classNames(...classes: (false | null | undefined | string)[]) {
  return classes.filter(Boolean).join(" ");
}

const PackageComparison: React.FC<PackageComparisonProps> = ({
  tiers,
  sections,
  title,
  subtitle,
  contactLink,
  language = APP_LANGUAGES.ENGLISH
}) => {
  return (
    <div id="PackageComparison-75" className="relative bg-[#FFFDF6] py-24 sm:py-32">
      <div className="mx-auto max-w-5xl">
        <div className="tile-title-container">
          <h2 className="tile-main-header">{title}</h2>
          <p className="tile-subtitle">{subtitle}</p>
          <p className="mt-4 text-center text-xl font-semibold">
            Get help choosing.{" "}
            <Link href={contactLink || "#"} className="ml-2 inline text-[#E5B04F]">
              <span>
                {" "}
                <Contact language={language} />
              </span>
              <ChevronRightIcon className="inline h-5 w-5" aria-hidden="true" />
            </Link>
          </p>
        </div>
      </div>
      {/* Feature comparison (up to lg) */}
      <section aria-labelledby="mobile-comparison-heading" className="lg:hidden">
        <h2 id="mobile-comparison-heading" className="sr-only">
          Feature comparison
        </h2>

        <div className=" grid max-w-2xl grid-cols-2 gap-4 px-4">
          {tiers?.map((tier) => (
            <div key={tier.id} className="col-span-1 mb-8 border-b border-b-gray-300">
              <h3
                className={classNames(
                  tier.featured ? "text-base-primary" : "text-base-primary",
                  "text-center text-xl font-medium leading-6"
                )}
              >
                {tier.name}
              </h3>
              <div
                className={classNames(
                  tier.featured ? "border-transparent" : "border-transparent",
                  "-mt-px w-full border-t-2 pt-2 text-center"
                )}
              >
                {!!tier.price?.total && (
                  <p className="mt-1 text-sm leading-6 text-gray-500">Original {tier.price?.total}</p>
                )}
                {!!tier.price?.discounted && (
                  <p className="mt-1 text-md leading-6 text-primary-700">Discounted {tier.price?.discounted}</p>
                )}
                <div className="w-full py-10">
                  <Link
                    href={tier.stripeLink || "#"}
                    className={getButtonVariantStylesAndSize({ variant: ButtonVariantTypes.DEFAULT, size: "sm" })}
                  >
                    Learn More
                  </Link>
                </div>
              </div>

              <div className="mt-2 space-y-10">
                {sections?.map((section) => (
                  <div key={section.name}>
                    <div className="relative mt-6">
                      <div className={classNames(tier.featured ? " ring-0" : "ring-0", "relative  bg-transparent ")}>
                        <dl className=" text-center text-md leading-6">
                          {section.features?.map((feature) => (
                            <div
                              key={feature.name}
                              className=" items-center justify-between px-4 py-3 text-center sm:grid sm:grid-cols-2 sm:px-0"
                            >
                              <dd className="mb-4 w-full">
                                {typeof feature.tiers?.[tier.id]?.content === "string" ? (
                                  <>
                                    {feature.tiers[tier.id].icon && (
                                      <div className="m-auto w-[40px]">
                                        <Image
                                          src={feature.tiers[tier.id].icon || ""}
                                          className="align-center mb-2"
                                          width={40}
                                          height={40}
                                          alt=""
                                        />
                                      </div>
                                    )}
                                    <div
                                      className={
                                        tier.featured ? "text-base-700 font-normal" : "text-base-700 font-normal"
                                      }
                                    >
                                      {feature.tiers[tier.id]?.content}
                                    </div>
                                  </>
                                ) : (
                                  <>
                                    {feature.tiers?.[tier.id]?.content === true ? (
                                      <CheckIcon className="mx-auto h-5 w-5 text-airbnbgreen" aria-hidden="true" />
                                    ) : (
                                      <MinusIcon className="mx-auto h-5 w-5 text-gray-400" aria-hidden="true" />
                                    )}

                                    <span className="sr-only">
                                      {feature.tiers?.[tier.id]?.content === true ? "Yes" : "No"}
                                    </span>
                                  </>
                                )}
                              </dd>
                            </div>
                          ))}
                        </dl>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Sticky header */}
      <div className="sticky top-0 z-10 mt-8 hidden  bg-[#FFFDF6] pt-4 sm:mt-10 lg:block ">
        <div className="mx-auto max-w-5xl">
          <div className="grid grid-cols-3 gap-x-10">
            {tiers?.map((tier) => (
              <div
                key={tier.id}
                className={classNames(
                  tier.featured ? "text-primary-700" : "text-primary-700",
                  "inline-flex items-center text-2xl font-semibold",
                  tier.featured ? "border-[#E5B04F]" : "border-gray-900/10",
                  "border-b-[1px] pb-4"
                )}
              >
                <span className="mr-2 flex h-14 w-14 shrink-0 items-center justify-center rounded-full border border-neutral-300 bg-[#F5F1E4]">
                  <Image src={tier.iconUrl || ""} alt="test" width={34} height={34} />
                </span>
                {tier.name}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Feature comparison (lg+) */}
      <section aria-labelledby="comparison-heading" className="hidden lg:block ">
        <div className="mx-auto max-w-5xl">
          <div className="grid grid-cols-3 gap-x-10 pb-24 sm:pb-32">
            {tiers?.map((tier) => (
              <div key={tier.id} aria-hidden="true" className="-mt-px ">
                <div className="pt-4">
                  <p className="mb-4 font-matter text-sm font-normal leading-6 text-primary-600">{tier.description}</p>
                  <div className="my-4 flex items-baseline  text-xl font-medium leading-6 text-gray-500">
                    {tier.price?.discounted}
                    <span className="ml-2 inline-block justify-end align-bottom  text-xl font-medium line-through">
                      {tier.price?.total}
                    </span>
                  </div>
                  <div className="w-full">
                    <Link
                      href={tier.stripeLink || "#"}
                      className={getButtonVariantStylesAndSize(
                        { variant: ButtonVariantTypes.DEFAULT, size: "default" },
                        "rounded-xl"
                      )}
                    >
                      Learn More
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="space-y-16 px-6 py-24 sm:py-32 lg:px-8">
          {sections?.map((section) => (
            <div key={section.name}>
              <div className="mx-auto max-w-5xl">
                <h3 className="mb-8 text-3xl font-semibold leading-6 text-base-primary">{section.name}</h3>
                <div className="grid grid-cols-3 gap-x-10  ">
                  {" "}
                  {tiers?.map((tier) => (
                    <div key={tier.id} aria-hidden="true" className="-mt-px ">
                      <p
                        className={classNames(
                          tier.featured ? "text-base-primary" : "text-base-primary",
                          "mb-4 text-2xl font-semibold"
                        )}
                      ></p>
                      <div
                        className={classNames(
                          tier.featured ? "border-[#E5B04F]" : "border-gray-900/10",
                          "border-t-2 pt-4"
                        )}
                      ></div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="relative -mx-8 mt-2">
                <div className="mx-auto max-w-5xl">
                  {section.features?.map((feature) => (
                    <div key={feature.name} className="grid grid-cols-3 gap-x-10">
                      {tiers?.map((tier) => (
                        <div key={tier.id} className="relative flex w-full items-center px-4 py-8 text-center">
                          <span className="relative w-full text-center font-medium">
                            {typeof feature.tiers?.[tier.id]?.content === "string" ? (
                              <>
                                {feature.tiers[tier.id].icon && (
                                  <div className="relative flex w-full justify-center pb-2 ">
                                    <Image
                                      src={feature.tiers[tier.id].icon || ""}
                                      className="mb-2"
                                      width={55}
                                      height={55}
                                      alt=""
                                    />
                                  </div>
                                )}
                                <div
                                  className={
                                    tier.featured
                                      ? "font-md mb-4 font-matter text-sm leading-6 text-primary-600"
                                      : "font-md mb-4 font-matter text-sm leading-6 text-primary-600"
                                  }
                                >
                                  {feature.tiers[tier.id]?.content}
                                </div>
                              </>
                            ) : (
                              <>
                                {feature.tiers?.[tier.id]?.content === true ? (
                                  <CheckIcon className="mx-auto h-5 w-5 text-airbnbgreen" aria-hidden="true" />
                                ) : (
                                  <MinusIcon className="mx-auto h-5 w-5 text-gray-400" aria-hidden="true" />
                                )}

                                <span className="sr-only">
                                  {feature.tiers?.[tier.id]?.content === true ? "Yes" : "No"}
                                </span>
                              </>
                            )}
                          </span>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default PackageComparison;
